import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { DEBOUNCE_USER_INPUT } from '@mopla/constants';
import {
	searchStopsOptions,
	type StopListResponseView,
	type StopResponseView,
} from '@mopla/ticketshop-api';

import { IStop } from '../types';

interface IUseStopSearchResult {
	stops: IStop[];
	/** True during the debounce window and while the request is in flight. */
	isSearchPending: boolean;
}

const mapStops = (res: StopListResponseView): IStop[] =>
	(res.stops ?? [])
		.filter(
			(s): s is StopResponseView & { id: string; name: string } =>
				!!s.id && !!s.name
		)
		.map((s) => ({ id: s.id, name: s.name, address: s.address || undefined }));

/**
 * Step 2b — operator entry-stop autocomplete. GET /operators/{operatorId}/stops
 *
 * Debounces the raw query, then queries the operator's stop list. Only
 * operator-specific stops are returned — this intentionally does NOT surface
 * myway / journey-search data. The BE returns an empty list for an empty
 * query, so we gate the request on a non-empty trimmed query FE-side.
 *
 * Results are never returned for an outdated query: while the debounce window
 * or the request is still open, `stops` is empty and `isSearchPending` is true,
 * so the caller shows neither the previous suggestions nor a premature
 * "no results" message.
 */
export const useStopSearch = (
	operatorId: string | undefined,
	query: string
): IUseStopSearchResult => {
	const trimmedQuery = query.trim();
	const [debouncedQuery, setDebouncedQuery] = useState('');

	useEffect(() => {
		const handle = setTimeout(
			() => setDebouncedQuery(trimmedQuery),
			DEBOUNCE_USER_INPUT
		);
		return () => clearTimeout(handle);
	}, [trimmedQuery]);

	/** Input moved on, query key has not — `data` still belongs to the previous query. */
	const isDebouncing = trimmedQuery !== debouncedQuery;
	const enabled = !!operatorId && debouncedQuery.length > 0;

	const { data, isFetching } = useQuery({
		...searchStopsOptions({
			path: { operatorId: operatorId ?? '' },
			query: { query: debouncedQuery },
		}),
		enabled,
		select: mapStops,
	});

	return {
		stops: isDebouncing ? [] : data ?? [],
		isSearchPending:
			trimmedQuery.length > 0 && (isDebouncing || (enabled && isFetching)),
	};
};
